import type { Guardrail, LaunchForm } from '@/api/schemas';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { PlatformIcon, Tag } from '@/components/badges';
import { cn } from '@/lib/utils';
import { useWording } from '@/lib/useAudience';
import { PLATFORM_LABEL } from './buildHelpers';
import { guardrailText, type CopyContext } from './runBuildCopy';
import { PlanView } from './PlanView';
import type { RunBuildFormModel } from './useRunBuildForm';

/** What will happen: guardrails from the launch form, then the dry-run plan. */
export function RunBuildAside({ form }: { form: RunBuildFormModel }) {
  const { count, preview, target } = form;
  const ctx: CopyContext = { commitSha: form.fields.values.commitSha, branch: form.fields.values.branch };
  return (
    <aside className="flex flex-col gap-4 border-t bg-secondary/40 p-4 sm:p-5 lg:border-l lg:border-t-0" aria-label="Preview">
      <div className="flex items-center justify-between">
        <Kicker className="text-subtle">
          <Icon name="visibility" size={16} />
          What will happen
        </Kicker>
        <Tag>
          {count} target{count === 1 ? '' : 's'}
        </Tag>
      </div>
      {target.launchData ? <LaunchSummary launch={target.launchData} general={form.generalGuardrails} lanes={target.queue.data?.lanes} ctx={ctx} /> : null}
      {preview ? (
        <PlanView result={preview} ctx={ctx} />
      ) : (
        <p className="text-xs text-subtle">Preview checks the build without starting it: version numbers, missing settings and checks. Nothing is queued.</p>
      )}
    </aside>
  );
}

type Lane = { providerAccount: { id: string; kind: string }; inUse: number; concurrencyLimit: number };

const APPROVAL: Guardrail = { code: 'HGR-APPROVAL-REQUIRED', level: 'warning', field: null, message: 'This environment needs an approval before the build runs' };
const FREEZE: Guardrail = { code: 'HGR-FREEZE-WINDOW', level: 'block', field: null, message: 'A freeze window is active for this environment' };

function LaunchSummary({ launch, general, lanes, ctx }: { launch: LaunchForm; general: Guardrail[]; lanes: Lane[] | undefined; ctx: CopyContext }) {
  return (
    <div className="flex flex-col gap-2">
      {launch.versionPreview?.map((v) => (
        <div key={v.platform} className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-[13px]">
          <PlatformIcon platform={v.platform} />
          <span className="text-foreground">{PLATFORM_LABEL[v.platform]}</span>
          <span className="tabular ml-auto font-mono text-xs text-muted-foreground">
            {v.versionName ?? '—'} · {v.platform === 'ios' ? 'build' : 'versionCode'} {v.nextBuildNumber ?? '—'}
          </span>
        </div>
      ))}
      {launch.requiresApproval ? <GuardrailRow g={APPROVAL} ctx={ctx} /> : null}
      {launch.activeFreezeWindow ? <GuardrailRow g={FREEZE} ctx={ctx} /> : null}
      {general.map((g, i) => (
        <GuardrailRow key={i} g={g} ctx={ctx} />
      ))}
      {lanes?.map((lane) => <QueueLane key={lane.providerAccount.id} lane={lane} />)}
    </div>
  );
}

function QueueLane({ lane }: { lane: Lane }) {
  const full = lane.inUse >= lane.concurrencyLimit;
  return (
    <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px]', full ? 'border-awaiting/30 bg-awaiting/10 text-awaiting' : 'bg-card text-muted-foreground')}>
      <Icon name="hourglass_empty" size={16} />
      <span>{full ? 'Waits for a free build slot' : 'A build slot is free now'}</span>
      <span className="tabular ml-auto text-xs" title={`${lane.inUse} of ${lane.concurrencyLimit} build slots in use`}>
        {lane.inUse}/{lane.concurrencyLimit}
      </span>
    </div>
  );
}

const GUARDRAIL_TONE = {
  block: { box: 'border-failed/30 bg-failed/10 text-failed', icon: 'block' },
  warning: { box: 'border-awaiting/30 bg-awaiting/10 text-awaiting', icon: 'warning' },
  info: { box: 'bg-card text-muted-foreground', icon: 'info' },
} as const;

/** One message about the build, in plain words (the API's code stays in `data-testid`, never on screen). */
function GuardrailRow({ g, ctx }: { g: Guardrail; ctx: CopyContext }) {
  const say = useWording();
  const tone = GUARDRAIL_TONE[g.level];
  return (
    <div data-testid={`guardrail-${g.code}`} className={cn('flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px]', tone.box)}>
      <Icon name={tone.icon} size={16} className="mt-px shrink-0" />
      <span className="min-w-0">{say(guardrailText(g, ctx))}</span>
    </div>
  );
}
